import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  useTheme,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { tokens } from "../../theme";

type Option = {
  data: any;
  label: string;
  value: string | number;
};

type CustomAutocompleteFieldProps = {
  name: string;
  label: string;
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium";
  sx?: object;
  control: any;
  fetchFunction?: (query: string, key?: string) => Promise<Option[]>;
  initialFetchFunction?: (query: string, key?: string) => Promise<Option[]>;
  transformResponse?: (response: any, woundCodeType?: string) => Option[];
  rules?: any;
  disabled?: boolean;
  onSelect?: (option: Option[] | null) => void;
  onChangeSelect?: (value: Option[] | Option | null) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  initialCodes?: string;
  woundCodeType?: string | "";
  isMultiple?: boolean;
  isFreeSolo?: boolean;
  placeholderText?: string | "";
};

const CustomAutocompleteField: React.FC<CustomAutocompleteFieldProps> = ({
  name,
  label,
  color,
  size = "medium",
  sx,
  control,
  fetchFunction,
  initialFetchFunction,
  transformResponse,
  rules,
  disabled,
  onSelect,
  onChangeSelect,
  onChange,
  initialCodes,
  woundCodeType,
  isMultiple,
  isFreeSolo,
  placeholderText,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const [isFocused, setIsFocused] = useState(false);
  const [returnedObjs, setReturnedObjs] = useState<any[]>([]);

  const minHeight = size === "small" ? "35px" : "53px";
  const dropdownScrollbarSx = {
    scrollbarGutter: "stable",
    scrollbarWidth: "thin",
    scrollbarColor: isDark
      ? "rgba(255,255,255,0.22) rgba(255,255,255,0.08)"
      : "#94A3B8 #E2E8F0",
    "&::-webkit-scrollbar": { width: "6px", height: "6px" },
    "&::-webkit-scrollbar-track": {
      background: isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0",
      borderRadius: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      background: isDark ? "rgba(255,255,255,0.22)" : "#94A3B8",
      borderRadius: 4,
    },
  };

  useEffect(() => {
    const fetchInitialOptions = async () => {
      if (initialCodes == undefined || !initialFetchFunction || !transformResponse) return;
      if (!initialCodes || initialCodes == null || initialCodes.length == 0) {
        setSelectedOptions([]);
      } else {
        const codeArray = Array.isArray(initialCodes)
          ? initialCodes
          : initialCodes.split(",").map((code) => code.trim());

        try {
          const promises = codeArray.map((code) =>
            initialFetchFunction(code, woundCodeType)
          );
          const responses = await Promise.all(promises);

          const allOptions = responses
            .flat()
            .map((resp) => transformResponse(resp, woundCodeType))
            .flat();

          setSelectedOptions(allOptions);

          const responseObjs = responses
            .map((resp) => (resp as any)?.data?.[woundCodeType ?? ""] || [])
            .flat();

          const finalObjs = [...responseObjs];
          setReturnedObjs(finalObjs);
          onSelect?.(finalObjs);
        } catch (error) {
          console.error("Error fetching initial codes:", error);
        }
      }
    };

    fetchInitialOptions();
  }, [initialCodes]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        fetchFunction &&
        !selectedOptions.some((option) => option.label === inputValue)
      ) {
        setLoading(true);
        try {
          const response = await fetchFunction(inputValue, woundCodeType);
          const responseObjs =
            (response as any)?.data?.[woundCodeType ?? ""] ?? [];
          setReturnedObjs(responseObjs);

          const transformedOptions = transformResponse
            ? transformResponse(response, woundCodeType)
            : [];

          setOptions(transformedOptions);
          if (isFocused) {
            setOpen(transformedOptions.length > 0);
          }
        } catch (error) {
          console.error("Autocomplete fetch error:", error);
          setOptions([]);
          setOpen(false);
        } finally {
          setLoading(false);
        }
      } else {
        setOptions([]);
        setOpen(false);
      }
    };

    fetchData();
  }, [inputValue]);

  const isRequired = () => {
    if (rules && typeof rules === "object" && "required" in rules && label) {
      return true;
    }
    return false;
  };

  const getLabel = () => {
    if (isRequired()) {
      return (
        <span>
          {label}{" "}
          <span style={{ color: colors.success[200], fontSize: "18px" }}>
            *
          </span>
        </span>
      );
    }
    return label;
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { ref, value, ...field }, fieldState }) => (
        <Autocomplete
          freeSolo={isFreeSolo}
          multiple={isMultiple}
          open={open}
          onOpen={() => setOpen(options.length > 0)}
          onClose={() => setOpen(false)}
          options={options}
          loading={loading}
          componentsProps={{
            paper: {
              sx: {
                ...dropdownScrollbarSx,
                "& .MuiAutocomplete-listbox": {
                  maxHeight: 260,
                  overflowY: "auto",
                  ...dropdownScrollbarSx,
                },
              },
            },
          }}
          ListboxProps={{
            sx: {
              maxHeight: 260,
              overflowY: "auto",
              ...dropdownScrollbarSx,
            },
          }}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
            if (isFreeSolo && !isMultiple) {
              field.onChange(newInputValue);
            }
            if (onChange) {
              onChange({
                target: {
                  name,
                  value: newInputValue,
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }
          }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.label
          }
          isOptionEqualToValue={(option, value) =>
            option.value === value?.value
          }
          value={
            isMultiple
              ? selectedOptions
              : typeof value === "string" && isFreeSolo
              ? value
              : selectedOptions?.[0] || null
          }
          onChange={(_, selected) => {
            if (isMultiple) {
              const selectedArray = Array.isArray(selected) ? selected : [];
              const filteredOptions = selectedArray.filter(
                (opt): opt is Option => typeof opt !== "string"
              );

              setSelectedOptions(filteredOptions);
              field.onChange(
                selectedArray.map((opt) =>
                  typeof opt === "string" ? opt : (opt as Option).value
                )
              );

              const selectedFullObjs = selectedArray.map((opt) => {
                const value =
                  typeof opt === "string" ? opt : (opt as Option).value;
                return returnedObjs.find((obj) => obj.woundKey === value);
              });

              onSelect?.(selectedFullObjs);
              onChangeSelect?.(selectedArray as Option[]);
            } else {
              const selectedOption = selected as Option | null;
              setSelectedOptions(selectedOption ? [selectedOption] : []);
              field.onChange(selectedOption?.value || null);
              const fullObj = returnedObjs.find(
                (obj) => obj.woundKey === selectedOption?.value
              );
              onSelect?.(fullObj ? [fullObj] : null);
              onChangeSelect?.(selectedOption);
            }
            setInputValue("");
            setIsFocused(false);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={getLabel() || ""}
              variant="outlined"
              size={size}
              color={
                color ||
                (theme.palette.mode === "dark" ? "secondary" : "primary")
              }
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={disabled}
              placeholder={placeholderText || ""}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              sx={{
                minHeight,
                "& .MuiInputBase-root": {
                  minHeight,
                },
                "& .MuiChip-root": {
                  margin: "0px 2px",
                },
                "& .MuiChip-root.Mui-disabled": {
                  margin: "0px 2px",
                  color: theme.palette.mode === "dark" ? "" : "black",
                },
                "& label": {
                  color:
                    theme.palette.mode === "dark" ? "" : colors.primary[100],
                },
                "& label.Mui-disabled": {
                  color:
                    theme.palette.mode === "dark" ? "" : colors.grey[400],
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor:
                      theme.palette.mode === "dark" ? "" : colors.primary[100],
                  },
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  "& fieldset": {
                    borderColor:
                      theme.palette.mode === "dark" ? "" : colors.grey[500],
                  },
                },
                // ← caller sx overrides last
                ...sx,
              }}
            />
          )}
        />
      )}
    />
  );
};

export default CustomAutocompleteField;